import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppBar, Avatar, Box, ButtonBase, Chip, Container, Grid, IconButton, Skeleton,
  Toolbar, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import Public from '@mui/icons-material/Public';
import Refresh from '@mui/icons-material/Refresh';
import IndicatorInfoDialog from './IndicatorInfoDialog';
import PanelCard from '../../design/PanelCard';
import MarketStatusChip from '../../design/MarketStatusChip';
import UpdatedStamp from '../../design/UpdatedStamp';
import { appBarGradient } from '../../design/theme';
import { useAuth } from '../../context/AuthContext';
import { useStockIndicators, VoterScore } from '../../hooks/useUniverse';

const CHART_HEIGHT = 260;

// Fixed layout of the ten charted indicators, in server chart order;
// a chart the server failed to render is absent from the payload, and its cell
// falls back to "Chart unavailable" instead of collapsing the grid.
const INDICATOR_NAMES = ['RSI', 'Bollinger Bands', 'MACD', 'ADX', 'DI Direction', 'Parabolic SAR',
                         'ATR', 'OBV', 'MA Anchor', 'RS vs SPY'];

// engine voter keys (processor .name values) -> the chart cards they annotate;
// ADX (referee) and ATR (veto-only) have charts but no vote.
const VOTER_KEY_BY_CHART: Record<string, string> = {
  RSI: 'RSI', 'Bollinger Bands': 'BollingerBands', MACD: 'MACD',
  'DI Direction': 'DI_DIRECTION', 'Parabolic SAR': 'ParabolicSAR', OBV: 'OBV',
  'MA Anchor': 'MA_ANCHOR', 'RS vs SPY': 'RS_SPY',
};
const FAMILY_LABEL = { trend: 'trend family', reversion: 'reversion family' } as const;

const IndicatorCharts: React.FC = () => {
  const { ticker = '' } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data, loading, error, refetch } = useStockIndicators(ticker);
  const [infoFor, setInfoFor] = useState<string | null>(null);

  const handleBack = () => {
    // react-router tracks its position in history.state.idx; 0 = first in-app entry
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(`/universe/${ticker}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const unavailable = error || (data && !data.available);
  const score = data?.available ? data.score : null;
  const chartByName = new Map((data?.charts ?? []).map((c) => [c.name, c.image]));

  // Complementary by construction so the equation's percentages always sum to 100
  // (independent rounding can produce e.g. 1% + 100% at w=0.005).
  const trendPct = score ? Math.round(score.blend_weight * 100) : 0;
  const reversionPct = 100 - trendPct;

  const voters = score?.voters ?? [];
  const voterByKey = new Map(voters.map((v) => [v.name, v]));
  const shareCaption = (v: VoterScore) => {
    const sum = voters.filter((x) => x.family === v.family).reduce((s, x) => s + x.weight, 0);
    return sum > 0 ? `${((v.weight / sum) * 100).toFixed(0)}% of ${FAMILY_LABEL[v.family]}` : '';
  };

  const annotationFor = (chartName: string): React.ReactNode => {
    if (!score) return null;
    if (chartName === 'ADX') {
      return (
        <Typography variant="caption" color="text.secondary">
          Referee — blends the families · {(score.blend_weight * 100).toFixed(0)}% trend
        </Typography>
      );
    }
    if (chartName === 'ATR') {
      const atrVeto = [...score.buy_veto_reasons, ...score.sell_veto_reasons]
        .some((r) => r.startsWith('ATR:'));
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {atrVeto && <Chip label="Veto active" color="warning" size="small" />}
          <Typography variant="caption" color="text.secondary">Veto-only — never votes</Typography>
        </Box>
      );
    }
    const v = voterByKey.get(VOTER_KEY_BY_CHART[chartName]);
    if (!v) return null; // stale server payload without voters: no annotation
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={`Score ${v.score.toFixed(0)}`} size="small" />
        <Typography variant="caption" color="text.secondary">{shareCaption(v)}</Typography>
      </Box>
    );
  };

  // Clickable card title: opens the static explainer dialog for that indicator.
  const titleButton = (label: string) => (
    <ButtonBase onClick={() => setInfoFor(label)} aria-label={`About ${label}`} sx={{ borderRadius: 1 }}>
      <Typography component="span" variant="h6"
                  sx={{ fontWeight: 700, color: 'primary.dark', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Typography>
    </ButtonBase>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0} sx={{ background: appBarGradient }}>
        <Toolbar>
          <IconButton color="inherit" onClick={handleBack} aria-label="Back" sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <IconButton onClick={() => navigate('/')} aria-label="Home" sx={{ p: 0, mr: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}><Public /></Avatar>
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {ticker.toUpperCase()} · Indicators
          </Typography>
          <Box sx={{ mr: 1 }}>
            <MarketStatusChip />
          </Box>
          <IconButton color="inherit" onClick={() => refetch()} aria-label="Refresh">
            <Refresh />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout} aria-label="Log out">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {loading ? (
          <>
            <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {INDICATOR_NAMES.map((name) => (
                <Grid item xs={12} md={6} key={name}>
                  <Skeleton variant="rounded" height={CHART_HEIGHT} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : unavailable ? (
          <PanelCard
            title="Indicators"
            error="Indicators unavailable — could not fetch or score this ticker."
            onRetry={refetch}
          >
            {null}
          </PanelCard>
        ) : data && (
          <>
            {score && (
              <Box sx={{ mb: 2 }}>
                {/* Neutral presentation by design: buy/sell verdicts depend on
                    per-trader thresholds, so the UI never colors the score. */}
                <PanelCard
                  title="Score"
                  action={<UpdatedStamp updatedAt={data.generated_at ? new Date(data.generated_at) : null} />}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5 }}>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 800 }}>
                        {score.score.toFixed(0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        0 = buy · 100 = sell
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label={`Trend ${score.trend_score.toFixed(0)}`} size="small" />
                      <Chip label={`Reversion ${score.reversion_score.toFixed(0)}`} size="small" />
                      <Chip label={`Blend ${(score.blend_weight * 100).toFixed(0)}% trend`}
                            size="small" variant="outlined" />
                      {score.veto_buy && (
                        <MuiTooltip title={score.buy_veto_reasons.join(', ')}>
                          <Chip label="Buy veto" color="warning" size="small" />
                        </MuiTooltip>
                      )}
                      {score.veto_sell && (
                        <MuiTooltip title={score.sell_veto_reasons.join(', ')}>
                          <Chip label="Sell veto" color="warning" size="small" />
                        </MuiTooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* the raw engine sentence (also in trader logs) stays as the tooltip */}
                      <MuiTooltip title={score.explanation}>
                        <Chip
                          label={score.blend_weight > 0.5 ? 'Trending market'
                            : score.blend_weight < 0.5 ? 'Ranging market' : 'Mixed regime'}
                          size="small" variant="outlined"
                        />
                      </MuiTooltip>
                      {typeof score.adx === 'number' && Number.isFinite(score.adx) && (
                        <Typography variant="caption" color="text.secondary">
                          ADX {score.adx.toFixed(0)}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {/* ≈, not =: the engine rounds the final score, so exact equality would lie */}
                      {`Final ${score.score.toFixed(0)} ≈ `
                        + `${trendPct}% × Trend ${score.trend_score.toFixed(0)}`
                        + ` + ${reversionPct}% × Reversion ${score.reversion_score.toFixed(0)}`}
                    </Typography>
                  </Box>
                </PanelCard>
              </Box>
            )}

            <Grid container spacing={2}>
              {INDICATOR_NAMES.map((name) => {
                const image = chartByName.get(name);
                return (
                  <Grid item xs={12} md={6} key={name}>
                    <PanelCard title={titleButton(name)} action={annotationFor(name)}>
                      {image ? (
                        <Box
                          component="img"
                          src={`data:image/png;base64,${image}`}
                          alt={`${name} chart for ${ticker.toUpperCase()}`}
                          sx={{ width: '100%', height: 'auto' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', py: 6, textAlign: 'center' }}>
                          Chart unavailable
                        </Typography>
                      )}
                    </PanelCard>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
        <IndicatorInfoDialog name={infoFor} onClose={() => setInfoFor(null)} />
      </Container>
    </Box>
  );
};

export default IndicatorCharts;
